import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const TEAM = [
  {
    name: 'Marie Mangouta',
    title: { fr: 'Consultante en stratégie et investissements', en: 'Real Estate Agent' },
    role: {
      fr: "Experte en gestion foncière et accompagnement des investisseurs.",
      en: 'Expert in land management and investor support.'
    },
    image: 'https://customer-assets.emergentagent.com/job_871d1a5d-4bd3-4990-915b-ab00daedcd13/artifacts/hrpc9jmr_Marie%20Mangoute.png',
  },
  {
    name: 'Nicolas Duchateau',
    title: { fr: 'Architecte', en: 'Architect' },
    role: {
      fr: "Garant de l'harmonie architecturale et de la conception des masterplans.",
      en: 'Guardian of architectural harmony and masterplan design.'
    },
    image: 'https://customer-assets.emergentagent.com/job_871d1a5d-4bd3-4990-915b-ab00daedcd13/artifacts/pad4bomj_Nicola%20Duchateau.png',
  },
  {
    name: 'Alain Cnrjac',
    title: { fr: 'Consultant Golf & Lifestyle', en: 'Golf & Lifestyle Consultant' },
    role: {
      fr: "Expert en aménagement de parcours et standard d'excellence sportive.",
      en: 'Expert in course design and sporting excellence standards.'
    },
    image: 'https://customer-assets.emergentagent.com/job_871d1a5d-4bd3-4990-915b-ab00daedcd13/artifacts/7nnd9ijy_Gemini_Generated_Image_410j74410j74410j.png',
  },
];

const TeamSection = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="equipe"
      ref={sectionRef}
      className="relative overflow-hidden"
      style={{ backgroundColor: '#00332b', padding: '100px 0' }}
      data-testid="team-section"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="w-12 h-px bg-emerald-500/40" />
            <span className="text-emerald-400/80 text-xs font-medium tracking-[0.3em] uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {language === 'fr' ? 'Notre Équipe' : 'Our Team'}
            </span>
            <span className="w-12 h-px bg-emerald-500/40" />
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
            {language === 'fr' ? 'Vos interlocuteurs ' : 'Your dedicated '}
            <span className="text-emerald-400">{language === 'fr' ? 'privilégiés' : 'contacts'}</span>
          </h2>
          <p
            className="mt-6 text-white/70 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {language === 'fr'
              ? "Une équipe d'experts dédiée à la réussite de votre patrimoine immobilier."
              : 'A team of experts dedicated to the success of your real estate assets.'}
          </p>
        </motion.div>

        {/* Single row — hover reveal */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {TEAM.map((member, index) => (
            <motion.div
              key={member.name}
              className="group relative cursor-pointer"
              initial={{ opacity: 0, y: 40 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.15 * index }}
              onMouseEnter={() => setHoveredIdx(index)}
              onMouseLeave={() => setHoveredIdx(null)}
              data-testid={`team-card-${index}`}
            >
              {/* Photo container */}
              <div
                className="relative overflow-hidden"
                style={{
                  borderRadius: '20px',
                  aspectRatio: '3 / 4',
                  boxShadow: hoveredIdx === index
                    ? '0 20px 50px rgba(0, 0, 0, 0.4)'
                    : '0 8px 30px rgba(0, 0, 0, 0.2)',
                  transition: 'box-shadow 0.4s ease',
                }}
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover object-top transition-transform duration-700 ease-out"
                  style={{
                    transform: hoveredIdx === index ? 'scale(1.05)' : 'scale(1)',
                  }}
                  loading="lazy"
                />

                {/* Gradient overlay — always visible at bottom for text */}
                <div
                  className="absolute inset-0 transition-opacity duration-500"
                  style={{
                    background: hoveredIdx === index
                      ? 'linear-gradient(to top, rgba(0,51,43,0.95) 0%, rgba(0,51,43,0.6) 45%, transparent 100%)'
                      : 'linear-gradient(to top, rgba(0,51,43,0.85) 0%, rgba(0,51,43,0.3) 35%, transparent 70%)',
                  }}
                />

                {/* Content — always at bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-5 lg:p-6 z-10">
                  {/* Title badge */}
                  <span
                    className="inline-block text-emerald-400 text-[10px] font-semibold uppercase tracking-[0.2em] mb-2 transition-transform duration-400"
                    style={{
                      fontFamily: "'Montserrat', sans-serif",
                      transform: hoveredIdx === index ? 'translateY(0)' : 'translateY(0)',
                      opacity: hoveredIdx === index ? 1 : 0.8,
                    }}
                  >
                    {member.title[language]}
                  </span>

                  {/* Name */}
                  <h3
                    className="font-display text-2xl lg:text-3xl text-white leading-tight transition-transform duration-400"
                    style={{
                      transform: hoveredIdx === index ? 'translateY(-4px)' : 'translateY(0)',
                    }}
                  >
                    {member.name}
                  </h3>

                  {/* Role — reveal on hover */}
                  <p
                    className="text-white/60 text-sm leading-relaxed mt-3 transition-all duration-500"
                    style={{
                      fontFamily: "'Montserrat', sans-serif",
                      maxHeight: hoveredIdx === index ? '80px' : '0',
                      opacity: hoveredIdx === index ? 1 : 0,
                      overflow: 'hidden',
                    }}
                  >
                    {member.role[language]}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
